import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DEFAULT_PRECISION,
  EXPIRY_OFFSET_HFT_DATE_FORMAT, MOMENT_OPTION_FORMAT, Utils
} from '@app/_helpers';
import { ConfigurationService, FilterOptions, LegPayoffObj, MARGIN_CALC_NOT_SUPPORTED, NgxToastrService, PortfolioService, SEARCH_SYMBOL_INSTRUMENT_TYPE, SearchSymbolResults, SymbolSearchService } from '@app/_shared';
import { EXCHANGE_STRINGS, EXCHANGE_STRINGS_FOR_CASH, LEG_SHORT_INSTRUMENT_TYPE, UNDERLYING_OPTIONS } from '@app/_shared/modules/portfolio-form/portfolio-form.enums';
import { LegDetail, PortfolioStrategy, StrategySymbol } from '@app/_shared/modules/portfolio-form/portfolio.interface';
import { StrategyModal } from '@app/_shared/modules/portfolio-form/portfolio.model';
import { EXECUTION_STYLE_NUM } from '@app/_shared/modules/strategy-form/strategy.enums';
import { MarketSocketService } from '@app/_shared/providers/websocket/market-socket/market-socket.service';
import { MarketIDMap } from '@app/_shared/shared.enums';
import * as moment from 'moment';
import { ExchangeId, OrderMode } from '../../../../../../utProto/enums_pb';
import { ExpiryType, StrikeSelectionType, UnderlyingType } from '../../../../../../utProto/webApiStrategyEnums_pb';
import { IndexScripMap } from '../../pay-off-curve/modules/rapid/providers/legs/legs.constants';
import { EXCHANGE_STRINGS_ENUM } from '../margin-calculator.constants';
import { MarginCalculatorRequest, MarginCalculatorResponse, MarginPortfolioObj, PeakMarginResponse } from './margin-calculator.interface';
import { BriefPortfolioModel } from '../../market-place/providers/market-place/market-place.models';
import { MyStrategyType } from '../../my-strategies/my-strategies.consts';
import { cloneDeep } from 'lodash-es';
import { MbPlansService } from '../../mb-plans/providers/mb-plans.service';
import { AlgowisePositionDataService } from '@app/_shared/providers/algowise-position-data/algowise-position-data.service';

export interface MarginCalculationResults {
  totalMargin: number;
  minCash: number;
}

@Injectable({
  providedIn: 'root'
})
export class MarginCalculatorService {
  ltpMap: Map<String, number> = new Map() as any;;

  constructor(
    private http: HttpClient,
    private conf: ConfigurationService,
    private marketSocket: MarketSocketService,
    private symbolSearchService: SymbolSearchService,
    private portfolioService: PortfolioService,
    private toastr: NgxToastrService,
    private mbPlansService: MbPlansService,
    private algoWisePositionService: AlgowisePositionDataService,
    private marketSocketService: MarketSocketService,
  ) { }


  calculateMargin(marginRequest: MarginCalculatorRequest) {
    return this.http.post<MarginCalculatorResponse>(this.conf.applicationUrls.calculateMargin, marginRequest);
  }

  calculatePeakMargin(marginRequest: MarginCalculatorRequest) {
    return this.http.post<PeakMarginResponse>(this.conf.applicationUrls.calculatePeakMargin, marginRequest);
  }

  calculateRequiredMargin(portfolioId: string) {
    return this.http.post<MarginCalculatorResponse>(`${this.conf.applicationUrls.calculateRequiredMargin}${portfolioId}`, {});
  }

  async getUnderlyingLTP(symbol: string, underlying: number, exchange: number) {

    let searchSymbol = symbol;
    let instrumentType = SEARCH_SYMBOL_INSTRUMENT_TYPE.COMMON_STOCK;
    let symbolObj: SearchSymbolResults;

    if (underlying == UnderlyingType.UNDERLYINGTYPE_FUTURE) {
      symbolObj = this.symbolSearchService.advanceSearch(`${EXCHANGE_STRINGS[exchange]} ${searchSymbol}`, instrumentType)[0];
    } else {
      if (IndexScripMap[symbol]) {
        searchSymbol = IndexScripMap[symbol];
        instrumentType = SEARCH_SYMBOL_INSTRUMENT_TYPE.INDEX;
        symbolObj = this.symbolSearchService.advanceSearch(`${EXCHANGE_STRINGS_FOR_CASH[exchange]} ${searchSymbol}`, instrumentType, FilterOptions.index)[0];
      }
      else {
        symbolObj = this.symbolSearchService.advanceSearch(`${EXCHANGE_STRINGS_FOR_CASH[exchange]} ${searchSymbol}`, instrumentType)[0];
      }
    }


    if (symbolObj) {
      const val: any = await this.marketSocket.getSingleUpdatedQuote(symbolObj.market_id, symbolObj.security_id, false);
      return val.LTP;
    }

    return null;

  }

  async prepareLegsForMarginCalculations(strategies: StrategyModal[] | PortfolioStrategy[], ltpMap: Map<String, number>, squareOffLegs: boolean = false) {
    let portfolioObjValid = true;
    const marginCalculatorRequest: MarginCalculatorRequest = {
      portfolios: []
    };
    let symbolToLegsMap: Map<String, MarginPortfolioObj> = new Map();
    for (let strat of strategies) {
      const strategy = new StrategyModal(strat);
      if (portfolioObjValid) {
        for (const leg of strategy.StrategyParams.legs) {
          // Skip dependent legs
          if(leg.orderExecutionType && leg.orderExecutionType === EXECUTION_STYLE_NUM.DEPENDENT) {
            continue;
          }
          const ltpMapKey = `${strategy.strategySymbol.symbol}_${strategy.underlying}`;
          let atmObj: SearchSymbolResults;
          let key: string;
          if (strategy.strategySymbol.exchange === ExchangeId.EXCHANGEID_NSECM || strategy.strategySymbol.exchange === ExchangeId.EXCHANGEID_BSECM){
            let symbols = this.symbolSearchService.advanceSearch(leg.legSymbol.symbol,SEARCH_SYMBOL_INSTRUMENT_TYPE.COMMON_STOCK);
            symbols = symbols.filter(symbol => symbol.instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.COMMON_STOCK 
                && symbol.market_id === EXCHANGE_STRINGS[leg.legSymbol.exchange]
            )
            atmObj = symbols[0];
            atmObj.lot_size = leg.lots;
          } else {
            atmObj = await this.calculateATM(strategy.strategySymbol, strategy.stepSize, leg, ltpMap.get(ltpMapKey), false, false, strategy.underlying) as SearchSymbolResults;
            atmObj.lot_size = this.symbolSearchService.getSymbolLots(MarketIDMap[atmObj.market_id], atmObj.security_id);
          }
          

          if (atmObj === null) {
            portfolioObjValid = false
            this.toastr.simpleCustomToaster(MARGIN_CALC_NOT_SUPPORTED);
            return;
          }

          key = `${atmObj.market_id}_${atmObj.security_id}`;

          if (squareOffLegs) {
            if (symbolToLegsMap.has(key)) {
              const obj = symbolToLegsMap.get(key);
              // If same symbol already exist in different strategy, just add/subtract its net qty.
              if (leg.orderMode == OrderMode.ORDERMODE_BUY) {
                obj.lots += leg.lots;
              } else {
                obj.lots -= leg.lots;
              }

              obj.order_mode = obj.lots > 0 ? OrderMode.ORDERMODE_BUY : OrderMode.ORDERMODE_SELL;

              if (!obj.lots) {
                symbolToLegsMap.delete(key);
              } else {
                symbolToLegsMap.set(key, obj);
              }
            } else {
              symbolToLegsMap.set(key, {
                security_id: atmObj.security_id,
                market_id: EXCHANGE_STRINGS_ENUM[atmObj.market_id],
                lots: leg.orderMode == OrderMode.ORDERMODE_BUY ? leg.lots : -leg.lots,
                order_mode: leg.orderMode,
                portfolio_id: symbolToLegsMap.size + 1
              });
            }
          } else {
            marginCalculatorRequest.portfolios.push({
              security_id: atmObj.security_id,
              market_id: EXCHANGE_STRINGS_ENUM[atmObj.market_id],
              lots: leg.orderMode == OrderMode.ORDERMODE_BUY ? leg.lots : -leg.lots,
              order_mode: leg.orderMode,
              portfolio_id: marginCalculatorRequest.portfolios.length + 1,
              lot_size: atmObj.lot_size
            });
          }

        };
      }
    };

    if (squareOffLegs) {
      marginCalculatorRequest.portfolios = Array.from(symbolToLegsMap.values());
    }

    return marginCalculatorRequest;
  }

  async calculateATM(
    strategySymbolObj: StrategySymbol, stepSize: number, leg: LegDetail, ltp: number,
    navigate = false, isMarginCalculator = false, underlying: number = UnderlyingType.UNDERLYINGTYPE_CASH): Promise<SearchSymbolResults | boolean> {

    let portfolioObjValid = true

    const exchange = EXCHANGE_STRINGS[parseInt(strategySymbolObj.exchange)];

    const optionMode = LEG_SHORT_INSTRUMENT_TYPE[leg.legSymbol.optionMode];

    let expectedExpiry;

    // In case of basic expiry, just convert the expiry into required format.
    if (leg.legSymbol.expiryType == ExpiryType.EXPIRYTYPE_BASIC) {
      expectedExpiry = leg.legSymbol.expiryDate;
      expectedExpiry = moment(leg.legSymbol.expiryOffset.toString(), EXPIRY_OFFSET_HFT_DATE_FORMAT).format(MOMENT_OPTION_FORMAT)
      expectedExpiry = expectedExpiry.toUpperCase();
    } else {
      // To fetch current expiry on the basis of given params.
      const expiries = this.symbolSearchService.getStrikePriceExpiryList(
        `${exchange} ${strategySymbolObj.symbol}`, SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION, optionMode,
        FilterOptions.basic);

      expectedExpiry = this.symbolSearchService.getSelectedExpiry(leg.legSymbol.expiryType, leg.legSymbol.expiryOffset, strategySymbolObj.symbol, expiries);
    }

    let atmObj: SearchSymbolResults = {} as SearchSymbolResults;

    if (strategySymbolObj.strikeSelection === StrikeSelectionType.STRIKESELECTIONTYPE_ATM) {
      // If ltp is not present for any underlying, not showing pay off curve as discussed.
      if (!ltp) {
        portfolioObjValid = false;
        if (navigate) {
          const exchange = EXCHANGE_STRINGS[parseInt(strategySymbolObj.exchange)];
          const isCashMarket = Utils.isCMSelected(exchange);
          if(!isCashMarket) {
            this.toastr.simpleCustomToaster(`Can't open ${isMarginCalculator ? 'margin calculator' : 'pay off curve'} as LTP not present for symbol ${strategySymbolObj.symbol}`);
          }
        } else {
          this.toastr.simpleCustomToaster(`Can't calculate margin as LTP not present for symbol ${strategySymbolObj.symbol}`);
        }

        return false;
      }

      atmObj = this.portfolioService.getAtmSymbolObject(
        strategySymbolObj.symbol, expectedExpiry, exchange,
        ltp, stepSize / 100, optionMode, leg.legSymbol.strikeValue / 100);
    } else if (strategySymbolObj.strikeSelection === StrikeSelectionType.STRIKESELECTIONTYPE_BASIC) {
      const exchangeCMSelected = Utils.isCMSelected(exchange);
      if (exchangeCMSelected) {
        let atmObjects = this.symbolSearchService.advanceSearch(
          `${exchange} ${strategySymbolObj.symbol}`, SEARCH_SYMBOL_INSTRUMENT_TYPE.COMMON_STOCK,
          FilterOptions.symbol
        );
        atmObj = atmObjects[0];
      } else {
        let atmObjects = this.symbolSearchService.advanceSearch(
          `${exchange} ${strategySymbolObj.symbol}`, SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION,
          FilterOptions.strike_price, optionMode, expectedExpiry
        );

        atmObj = atmObjects.find((ele => parseFloat(ele.strike_price.toString()) === (leg.legSymbol.strikeValue / 100)));
      }
    } else {
      /**
       * if strikeSelection is price then sending the price entered by user to mdl, for fetching strike price related
       * to the price entered.
       */
      const response = await this.portfolioService.getSymbolForStrikePrice(leg, expectedExpiry, strategySymbolObj, underlying);
      if (response) {
        atmObj = response;
      }
    }

    return portfolioObjValid && atmObj;

  }

  async sendStrikePriceRequest(portfolio: BriefPortfolioModel) {
    this.ltpMap = new Map();
    for (let strategy of portfolio.strategies) {
      await this.setSelectedSymbol(strategy.strategySymbol.symbol, strategy.underlying, portfolio);
    };

  }

  async prepareLegsCalculation(isMarginCalculator = false, navigate = true, portfolio: BriefPortfolioModel, type: MyStrategyType , runningStrategyIds: string[], isExchangeCM: boolean) {
    if (!(portfolio.isPublic || portfolio.isCopyOfOriginals)) {
      await this.sendStrikePriceRequest(portfolio);
    }

    let payoffObj = {
      portfolioId: portfolio.portfolioID,
      portfolioName: portfolio.portfolioName,
      strategies: [],
      exchange: portfolio.strategies[0].strategySymbol.exchange?.toString()
    };

    let symbolToLegsMap: Map<String, MarginPortfolioObj> = new Map();

    let portfolioObjValid = true;

    for (const [stgIdx, strategy] of portfolio.strategies.entries()) {
      // skip dependent strategies
      if (portfolioObjValid && strategy.orderExecutionType !== EXECUTION_STYLE_NUM.DEPENDENT) {
        let legsArray: LegPayoffObj[] = [];
        const legPositions = Array.from(this.algoWisePositionService.getPortfoiloStrategyLegPosition(portfolio.uniquePortfolioID, runningStrategyIds ? runningStrategyIds[stgIdx] : ''));
        for (let leg of strategy.StrategyParams.legs) {

          // Skip dependent legs
          if(leg.orderExecutionType && leg.orderExecutionType === EXECUTION_STYLE_NUM.DEPENDENT) {
            continue;
          }
          let legId = leg.legId.toString();
          let tempMap = new Map();
          let atmObj: SearchSymbolResults

          legPositions.forEach((value) => {
            let splitArray = value[0].split('_');
            let legIdFromMap = splitArray[splitArray.length - 1];
            tempMap.set(legIdFromMap, value[1]);
          })
          // Get symbol objects from positions if opened from deployed & positions available
          if (type === MyStrategyType.Deployed && tempMap.has(legId)) {
            const legPosition = tempMap.get(legId);
            const symbolID = Utils.getSymbolId(+MarketIDMap[legPosition.market_id], legPosition.security_id);
            atmObj = this.symbolSearchService.getSymbolForSymbolId(symbolID);
          } else {
            let symbolData = isExchangeCM ? leg.legSymbol.symbol : strategy.strategySymbol.symbol;
            const ltpMapKey = `${symbolData}_${strategy.underlying}`;
            atmObj = await this.calculateATM(leg.legSymbol, strategy.stepSize, leg, this.ltpMap.get(ltpMapKey), navigate, isMarginCalculator, strategy.underlying) as SearchSymbolResults;
          }
          if (atmObj === null) {
            portfolioObjValid = false
            this.toastr.simpleCustomToaster(`Margin calculation not supported for strike selection type price.`);
            return;
          }

          const key = `${atmObj.market_id}_${atmObj.security_id}`;
          if (symbolToLegsMap.has(key)) {
            const obj = symbolToLegsMap.get(key);
            // If same symbol already exist in different strategy, just add/subtract its net qty.
            if (leg.orderMode == OrderMode.ORDERMODE_BUY) {
              obj.lots += leg.lots;
            } else {
              obj.lots -= leg.lots;
            }

            obj.order_mode = obj.lots > 0 ? OrderMode.ORDERMODE_BUY : OrderMode.ORDERMODE_SELL;

            if (!obj.lots) {
              symbolToLegsMap.delete(key);
            } else {
              symbolToLegsMap.set(key, obj);
            }
          } else {
            symbolToLegsMap.set(key, {
              security_id: atmObj.security_id,
              market_id: EXCHANGE_STRINGS_ENUM[atmObj.market_id],
              lots: leg.orderMode == OrderMode.ORDERMODE_BUY ? leg.lots : -leg.lots,
              order_mode: leg.orderMode,
              portfolio_id: symbolToLegsMap.size + 1
            });
          }

          legsArray.push({
            lots: leg.lots,
            orderMode: leg.orderMode,
            symbol: atmObj,
            ...((tempMap && tempMap.has(legId) && (type === MyStrategyType.Deployed)) && {
              price: leg.orderMode ?
                +Utils.applyPrecision(tempMap.get(legId)?.averageSellPrice, DEFAULT_PRECISION).toFixed(DEFAULT_PRECISION) :
                +Utils.applyPrecision(tempMap.get(legId).averageBuyPrice, DEFAULT_PRECISION).toFixed(DEFAULT_PRECISION)
            })

          });

        };

        let symbolObj: SearchSymbolResults;

        if (strategy.underlying.toString() == UnderlyingType.UNDERLYINGTYPE_FUTURE.toString()) {
          symbolObj = this.symbolSearchService.advanceSearch(`${EXCHANGE_STRINGS[parseInt(portfolio.strategies[0].strategySymbol.exchange)]} ${strategy.strategySymbol.symbol}`, SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE)[0];
        } else {
          if (IndexScripMap[strategy.strategySymbol.symbol]) {
            const searchedSymbol = IndexScripMap[strategy.strategySymbol.symbol]
            const instrumentType = SEARCH_SYMBOL_INSTRUMENT_TYPE.INDEX;
            symbolObj = this.symbolSearchService.advanceSearch(`${EXCHANGE_STRINGS_FOR_CASH[parseInt(portfolio.strategies[0].strategySymbol.exchange)]} ${searchedSymbol}`, instrumentType, FilterOptions.index)[0];
          } else {
            symbolObj = this.symbolSearchService.advanceSearch(`${EXCHANGE_STRINGS_FOR_CASH[parseInt(portfolio.strategies[0].strategySymbol.exchange)]} ${strategy.strategySymbol.symbol}`, SEARCH_SYMBOL_INSTRUMENT_TYPE.COMMON_STOCK)[0];
          }
        }


        payoffObj.strategies.push({
          strategyName: strategy.strategyName ? strategy.strategyName : strategy.AlgoName,
          underLyingType: UNDERLYING_OPTIONS[strategy.underlying].toLowerCase(),
          symbol: cloneDeep(symbolObj),
          legs: legsArray
        });
        // }
      }
    };

    if (portfolioObjValid) {
      const array = Array.from(symbolToLegsMap.values());
      /*We need the above calculations perfomed to calculate margin in the
      portfolio params and to send to margin calculator as well
      */
      let marginObj = array;
    }

    return payoffObj;

  }

  async setSelectedSymbol(symbol: string, underlying: number, portfolio: BriefPortfolioModel) {
    this.ltpMap.set(`${symbol}_${underlying}`, null);

    let searchSymbol = symbol;
    let instrumentType = SEARCH_SYMBOL_INSTRUMENT_TYPE.COMMON_STOCK;
    let symbolObj: SearchSymbolResults;

    if (underlying == UnderlyingType.UNDERLYINGTYPE_FUTURE) {
      symbolObj = this.symbolSearchService.advanceSearch(`${EXCHANGE_STRINGS[parseInt(portfolio.strategies[0].strategySymbol.exchange)]} ${searchSymbol}`, instrumentType)[0];
    } else {
      if (IndexScripMap[symbol]) {
        searchSymbol = IndexScripMap[symbol];
        instrumentType = SEARCH_SYMBOL_INSTRUMENT_TYPE.INDEX;
        symbolObj = this.symbolSearchService.advanceSearch(`${EXCHANGE_STRINGS_FOR_CASH[parseInt(portfolio.strategies[0].strategySymbol.exchange)]} ${searchSymbol}`, instrumentType, FilterOptions.index)[0];
      }
      else {
        symbolObj = this.symbolSearchService.advanceSearch(`${EXCHANGE_STRINGS_FOR_CASH[parseInt(portfolio.strategies[0].strategySymbol.exchange)]} ${searchSymbol}`, instrumentType)[0];
      }
    }

    if (symbolObj) {
      const val: any = await this.marketSocketService.getSingleUpdatedQuote(symbolObj.market_id, symbolObj.security_id);
      this.ltpMap.set(`${symbol}_${underlying}`, val.LTP);
    }

  }
}
